import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import MobileViewContainer from "../../Components/MobileViewContainer";
import { useAuthContext } from "../../Components/AuthContext";
import { API } from "../../api";
import { numberWithCommas } from "../../common/utils/format_util";
import PackageDetailSection from "./packageDetailSection";

const pad2 = (value) => String(value).padStart(2, "0");

const formatOfferDate = (value) => {
  if (!value) return "제한 없음";
  return String(value).replaceAll("-", ".");
};

const formatOfferDateTime = (value) => {
  if (!value) return "제한 없음";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "제한 없음";
  return `${date.getFullYear()}.${pad2(date.getMonth() + 1)}.${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const formatPerUserLimit = (value) => {
  const count = Number(value || 0);
  return count > 0 ? `${count}회` : "제한 없음";
};

function PrivatePackageOfferPage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthContext();
  const [detailProgressPercent, setDetailProgressPercent] = useState(44);
  const [headerTitle, setHeaderTitle] = useState("예약상품선택");
  const [offer, setOffer] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (accessToken || !token) return;
    const redirectPath = `${location.pathname}${location.search}`;
    navigate(`/login?path=${encodeURIComponent(redirectPath)}`, { replace: true });
  }, [accessToken, location.pathname, location.search, navigate, token]);

  useEffect(() => {
    if (!accessToken || !token) return;
    setLoading(true);
    API.get(`/package/private-offers/${token}/`)
      .then((response) => {
        setOffer(response.data);
      })
      .catch(() => {
        setOffer({ packages: [] });
      })
      .finally(() => setLoading(false));
  }, [accessToken, token]);

  if (!token || !accessToken) return null;

  const isOptionStep = searchParams.get("step") === "options";
  const handleBack = () => {
    if (isOptionStep) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("step");
      navigate({
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      });
      return;
    }
    if (selectedItem) {
      setSelectedItem(null);
      setHeaderTitle("예약상품선택");
      setDetailProgressPercent(28);
      return;
    }
    navigate("/");
  };

  const renderOfferGuide = () => (
    <div className="mb-4 rounded-2xl border border-[#f0d8c3] bg-white px-4 py-4 shadow-sm">
      {offer?.guide_message ? (
        <div className="mb-4 whitespace-pre-wrap rounded-xl bg-[#fff7f4] px-3 py-3 text-sm font-semibold leading-6 text-[#9f3d1f]">
          {offer.guide_message}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
        <div>
          <div className="text-xs font-bold text-gray-500">특별가 시작</div>
          <div className="mt-1 font-semibold text-gray-900">{formatOfferDateTime(offer?.starts_at)}</div>
        </div>
        <div>
          <div className="text-xs font-bold text-gray-500">특별가 종료</div>
          <div className="mt-1 font-semibold text-gray-900">{formatOfferDateTime(offer?.ends_at)}</div>
        </div>
        <div>
          <div className="text-xs font-bold text-gray-500">예약가능 시작일</div>
          <div className="mt-1 font-semibold text-gray-900">{formatOfferDate(offer?.reservation_start_date)}</div>
        </div>
        <div>
          <div className="text-xs font-bold text-gray-500">예약가능 종료일</div>
          <div className="mt-1 font-semibold text-gray-900">{formatOfferDate(offer?.reservation_end_date)}</div>
        </div>
        <div className="col-span-2 border-t border-gray-100 pt-3">
          <div className="text-xs font-bold text-gray-500">고객별 예약가능 회수</div>
          <div className="mt-1 font-semibold text-gray-900">{formatPerUserLimit(offer?.per_user_limit)}</div>
        </div>
      </div>
    </div>
  );

  const renderOfferList = () => (
    <div className="min-h-[calc(100vh-92px)] bg-gray-50 px-4 pb-10 pt-4">
      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center text-sm font-semibold text-gray-500">불러오는 중</div>
      ) : null}
      {!loading && offer?.token ? renderOfferGuide() : null}
      {!loading && !offer?.packages?.length ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-8 text-center text-sm font-semibold text-gray-500">
          구매 가능한 상품이 없습니다.
        </div>
      ) : null}
      <div className="space-y-3">
        {(offer?.packages || []).map((item) => (
          <button
            key={item.item_id || item.package.id}
            type="button"
            onClick={() => setSelectedItem(item)}
            className="block w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-sm transition active:scale-[0.99]"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="truncate text-base font-bold text-gray-900">{item.package.name}</div>
                {item.package.tagline ? (
                  <div className="mt-1 truncate text-xs font-medium text-gray-500">{item.package.tagline}</div>
                ) : null}
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-bold text-gray-900">평일 {numberWithCommas(item.weekday_price || item.price)}원</div>
                <div className="mt-1 text-xs font-semibold text-[#e16036]">주말·공휴일 {numberWithCommas(item.weekend_holiday_price || item.price)}원</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <MobileViewContainer>
      <div className="fixed top-0 left-1/2 z-50 w-full max-w-[640px] -translate-x-1/2 border-b border-gray-200/70 bg-white/85 px-4 pb-3 pt-[14px] backdrop-blur-md">
        <div className="grid h-10 grid-cols-[40px_1fr_40px] items-center">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center text-gray-700 transition-all hover:border-gray-300 active:scale-95"
            aria-label="뒤로가기"
          >
            <ChevronLeft size={22} strokeWidth={2.2} />
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold leading-tight text-gray-900">{headerTitle}</h1>
          </div>
          <div aria-hidden="true" />
        </div>
        <div className="relative mt-3 h-1.5 rounded-full bg-gray-200">
          <div
            className="progress-shimmer h-full w-full origin-left rounded-full bg-[linear-gradient(90deg,#f4c8cf_0%,#f7b08a_42%,#e16036_100%)] transition-transform duration-500 ease-out"
            style={{ transform: `scaleX(${detailProgressPercent / 100})` }}
          />
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#d95a34] shadow-[0_0_0_2px_rgba(225,96,54,0.08)] transition-[left] duration-500 ease-out"
            style={{ left: `${detailProgressPercent}%` }}
          />
        </div>
      </div>

      <div className="pt-[92px]">
        {selectedItem ? (
          <PackageDetailSection
            id={String(selectedItem.package.id)}
            privateOfferToken={token}
            privateOfferItemId={String(selectedItem.item_id)}
            privateOfferReservationStartDate={offer?.reservation_start_date || ""}
            privateOfferReservationEndDate={offer?.reservation_end_date || ""}
            initialProduct={{
              ...selectedItem.package,
              price: selectedItem.price,
              private_offer_token: token,
              private_offer_item_id: selectedItem.item_id,
            }}
            onProgressChange={setDetailProgressPercent}
            onHeaderTitleChange={setHeaderTitle}
          />
        ) : renderOfferList()}
      </div>
    </MobileViewContainer>
  );
}

export default PrivatePackageOfferPage;
